/**
 * Task Drawer Footer
 *
 * Sticky footer for the task drawer with assigned-to selection and
 * SplitButton action menu (primary action + cancel/edit/delete).
 *
 * @module features/tasks/components/TaskDrawerFooter
 */

import React from 'react';
import { Flex, Text, Button, SplitButton, Dialog, DialogTrigger, DialogFooter, DrawerFooter, useToast } from '@chatsdet/ui';
import { Trash } from '@phosphor-icons/react';
import { TeamMemberSelect } from '../../../ui/components/TeamMemberSelect';
import { useOrgMembers } from '../../../hooks/useOrgMembers';
import {
  useCompleteTask,
  useCancelTask,
  useTriggerTask,
  useClaimTask,
  useUpdateTask,
  useDeleteTask,
} from '../hooks/useTasks';
import type { AgentTaskDetail } from '../types';

interface TaskDrawerFooterProps {
  task: AgentTaskDetail;
  onDeleted: () => void;
}

export function TaskDrawerFooter({ task, onDeleted }: TaskDrawerFooterProps) {
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  const completeTask = useCompleteTask();
  const cancelTask = useCancelTask();
  const triggerTask = useTriggerTask();
  const claimTask = useClaimTask();
  const updateTask = useUpdateTask();
  const deleteTask = useDeleteTask();
  const { toast } = useToast();
  const membersQuery = useOrgMembers();
  const members = membersQuery.data?.items ?? [];

  const canEdit =
    task.status !== 'completed' && task.status !== 'canceled' && task.status !== 'expired';
  const canComplete = task.status === 'actionable' || task.status === 'in_progress';
  const canCancel =
    task.status === 'waiting' || task.status === 'actionable' || task.status === 'in_progress';
  const canTrigger = task.status === 'waiting';
  const canClaim = task.status === 'actionable';

  const isActionPending =
    completeTask.isPending ||
    cancelTask.isPending ||
    triggerTask.isPending ||
    claimTask.isPending ||
    deleteTask.isPending;

  // Resolve assignedTo email to userId for TeamMemberSelect
  const assignedUserId = React.useMemo(() => {
    if (!task.assignedTo) return null;
    const member = members.find(
      (m) => m.primaryEmail === task.assignedTo || m.displayName === task.assignedTo
    );
    return member?.userId ?? null;
  }, [task.assignedTo, members]);

  const handleAssignmentChange = (userId: string | null) => {
    if (!userId) {
      updateTask.mutate({ id: task.id, assignedTo: null });
      return;
    }
    const member = members.find((m) => m.userId === userId);
    const assignedTo = member?.primaryEmail ?? member?.displayName ?? userId;
    updateTask.mutate({ id: task.id, assignedTo });
  };

  // Determine primary action
  const primaryAction = canTrigger
    ? { label: 'Trigger', handler: () => triggerTask.mutate(task.id) }
    : canClaim
      ? { label: 'Claim', handler: () => claimTask.mutate(task.id) }
      : canComplete
        ? { label: 'Complete', handler: () => completeTask.mutate({ id: task.id }) }
        : null;

  return (
    <>
      <DrawerFooter className="shadow-[0_-2px_12px_-6px_rgba(0,0,0,0.12)]">
        <Flex align="center" justify="between" gap="md" wrap className="w-full">
          <Flex align="center" gap="sm" className="flex-1 min-w-0">
            <Text size="sm" className="text-muted-foreground shrink-0">
              Assigned to
            </Text>
            <TeamMemberSelect
              selectedUserId={assignedUserId}
              onSelect={handleAssignmentChange}
              isDisabled={updateTask.isPending || !canEdit}
              className="min-w-[200px] max-w-[300px]"
            />
          </Flex>
          {primaryAction ? (
            <SplitButton
              variant="primary"
              size="sm"
              onPress={primaryAction.handler}
              isDisabled={isActionPending}
            >
              {primaryAction.label}
              {canCancel && (
                <SplitButton.Item key="cancel" onAction={() => cancelTask.mutate(task.id)}>
                  Cancel Task
                </SplitButton.Item>
              )}
              <SplitButton.Separator />
              <SplitButton.Item
                key="delete"
                destructive
                onAction={() => setDeleteConfirmOpen(true)}
              >
                <Flex align="center" gap="xs">
                  <Trash size={14} />
                  Delete
                </Flex>
              </SplitButton.Item>
            </SplitButton>
          ) : (
            <Button
              variant="outline"
              size="sm"
              iconLeft={<Trash size={14} />}
              onPress={() => setDeleteConfirmOpen(true)}
              isDisabled={deleteTask.isPending}
            >
              Delete
            </Button>
          )}
        </Flex>
      </DrawerFooter>

      {/* Delete confirmation */}
      <DialogTrigger isOpen={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <span style={{ display: 'none' }} />
        <Dialog title="Delete Task">
          <Text>Are you sure you want to delete this task? This action cannot be undone.</Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                onPress={async () => {
                  try {
                    await deleteTask.mutateAsync(task.id);
                    toast.success('Task deleted');
                    setDeleteConfirmOpen(false);
                    onDeleted();
                  } catch {
                    toast.error('Failed to delete task');
                  }
                }}
                isDisabled={deleteTask.isPending}
              >
                {deleteTask.isPending ? 'Deleting...' : 'Delete'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>

    </>
  );
}

